'use client';

/** One page wake. Foreground returns carry one renewal edge shared by every subscriber;
 *  focus and duplicate lifecycle signals carry only a health check. `hiddenMs` also covers
 *  pagehide/freeze when the browser omits visibilitychange. */
export interface ReviveEvent {
  hiddenMs: number;
  /** One visible return after hiding/freezing, not repeated focus or wake signals. */
  foregroundReturned: boolean;
  /** A real offline-to-online edge, independent of page visibility. */
  networkRecovered: boolean;
}

/** A hide shorter than this, on a stream that still looks healthy, needs no recovery — anything longer may
 *  have lost frames. Shared by every stream so "was the page away long enough to matter" has one answer. */
export const STALE_HIDE_MS = 5_000;

type ReviveListener = (event: ReviveEvent) => void;

/** Repeated wake signals for ONE wake-up (pageshow + visibilitychange + focus all fire when a phone is
 *  unlocked) collapse into a single event inside this window. */
const COALESCE_MS = 1_000;

const listeners = new Set<ReviveListener>();
let installed = false;
let hiddenSince: number | null = null;
let lastPublishedAt = 0;
let offline = false;
// The first pageshow/resume can be the only observed restore signal; later returns require a hide.
let foregroundPending = true;

function publish(networkRecovered = false, foregroundSignal = false): void {
  if (document.hidden && !networkRecovered) return;
  const now = Date.now();
  const foregroundReturned = foregroundSignal && foregroundPending;
  if (!networkRecovered && !foregroundReturned && now - lastPublishedAt < COALESCE_MS) return;
  lastPublishedAt = now;
  const event: ReviveEvent = {
    hiddenMs: hiddenSince === null ? 0 : Math.max(0, now - hiddenSince),
    networkRecovered, foregroundReturned,
  };
  if (foregroundReturned) {
    foregroundPending = false;
    hiddenSince = null;
  }
  for (const listener of [...listeners]) listener(event);
}

const onHide = (): void => {
  hiddenSince ??= Date.now();
  foregroundPending = true;
};
const onVisibilityChange = (): void => {
  if (document.hidden) { onHide(); return; }
  publish(false, true);
};
// pagehide/pageshow also cover bfcache-ineligible phone locks; persisted is not a recovery gate.
const onPageShow = (): void => publish(false, true);
const onFocus = (): void => publish();
const onOffline = (): void => { offline = true; };
const onOnline = (): void => {
  const recovered = offline;
  offline = false;
  publish(recovered);
};

function install(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  offline = !navigator.onLine;
  foregroundPending = true;
  hiddenSince = document.hidden ? Date.now() : null;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('pagehide', onHide);
  document.addEventListener('freeze', onHide);
  document.addEventListener('resume', onPageShow);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
}

function uninstall(): void {
  if (!installed) return;
  installed = false;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pageshow', onPageShow);
  window.removeEventListener('pagehide', onHide);
  document.removeEventListener('freeze', onHide);
  document.removeEventListener('resume', onPageShow);
  window.removeEventListener('focus', onFocus);
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
  hiddenSince = null;
  offline = false;
  lastPublishedAt = 0;
}

/** Subscribe to page wake-ups. The DOM listeners are installed ONCE for the whole app and torn down with
 *  the last subscriber: the events are global, so a per-hook listener set would mean every stream
 *  reconnecting on its own uncoordinated copy of the same signal. WHAT to recover stays with each stream
 *  — only the detection is shared. Returns the unsubscribe. */
export function subscribeRevive(listener: ReviveListener): () => void {
  install();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) uninstall();
  };
}
